import geojsonLayer1 from '../../../assets/data/base-des-lieux-et-des-equipements-culturels-part1.geojson';
import geojsonLayer2 from '../../../assets/data/base-des-lieux-et-des-equipements-culturels-part2.geojson';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import markerV2 from '../../../assets/images/marker_v2.png';
import markerShadow from '../../../assets/images/marker-shadow.png';
import shp from 'shpjs';
import franceShapefileLayer from '../../../assets/data/france_only_layer.zip';
import Cookies from 'js-cookie';
import franceLayers from '../../../assets/data/Protected_areas.zip';

interface LayerAttributes {
  name: string;
  code: string;
  url: string;
}

const LAYER_MAPPINGS: { [key: string]: LayerAttributes } = {
  // 3.2 Natura 2000 SIC & ZPS Layers
  'Sites classés au titre de la Directive Habitats : périmètres transmis à la CE': { name: 'SITENAME', code: 'SITECODE', url: 'URL_FICHE' },
  'Zones de protection spéciale': { name: 'SITENAME', code: 'SITECODE', url: 'URL_FICHE' },

  // 3.3 ZNIEFF Type 1 & Type 2 Layers
  'Zones Naturelles d’Intérêt Ecologique Faunistique et Floristique de type 1': { name: 'NOM', code: 'ID_MNHN', url: 'URL_FICHE' },
  'Zones Naturelles d’Intérêt Ecologique Faunistique et Floristique de type 2': { name: 'NOM', code: 'ID_MNHN', url: 'URL_FICHE' },

  // 3.1 General Layers (Default)
  DEFAULT: { name: 'NOM_SITE', code: 'ID_MNHN', url: 'URL_FICHE' },
};

// 3.4 Default Visible Layers (For performance/lazy-loading)
const DEFAULT_VISIBLE_LAYERS = [
  'Arretés de protection de biotope',
  'Espaces naturels sensibles',
  'Parcs naturels régionaux',
  'Réserves naturelles nationales',
  'Réserves naturelles régionales',
  'Sites Ramsar',
  'Sites classés au titre de la Directive Habitats : périmètres transmis à la CE',
  "Terrains des Conservatoires d'Espaces Naturels",
  'Terrains du Conservatoire du Littoral',
  'Zones de protection spéciale',
  'Zones Naturelles d’Intérêt Ecologique Faunistique et Floristique de type 1',
  'Zones Naturelles d’Intérêt Ecologique Faunistique et Floristique de type 2',
];

// --- Leaflet Icon Setup ---
L.Icon.Default.mergeOptions({
  iconUrl: markerV2,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
  shadowUrl: markerShadow,
  shadowSize: [41, 41],
  shadowAnchor: [16, 41],
});

class GeoJsonControl extends L.Control {
  private map: L.Map | null = null;
  private selectedArea: 'useCase' | 'world' | 'none' = 'none';
  private franceMetropolitanLayer: L.LayerGroup | null = null;
  private franceProtectedLayerGroup: L.LayerGroup | null = null;
  private protectedLayersMap: Map<string, L.GeoJSON> = new Map();
  private showLegend: boolean = false;
  private protectedControl: L.Control.Layers | null = null;
  private isLoading: boolean = false;
  private legendControl: L.Control | null = null;

  onAdd(map: L.Map): HTMLElement {
    this.map = map;
    this.addData();
    return this.createLegendDiv();
  }

  private addLegend() {
    if (!this.map) return;

    // Remove existing legends to avoid duplicates
    this.map.eachLayer((layer) => {
      if (layer instanceof L.Control) {
        this.map!.removeControl(layer);
      }
    });

    this.legendControl = new L.Control({ position: 'bottomleft' });

    this.legendControl.onAdd = (map: L.Map) => {
      const div = L.DomUtil.create('div', 'leaflet-control-france-usecase');
      div.style.left = '0px';
      div.style.backgroundColor = 'white';
      div.style.padding = '10px';
      div.style.borderRadius = '8px';
      div.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
      div.style.width = '220px';
      div.style.zIndex = '1000';

      const updateLegendContent = () => {
        div.innerHTML = `
          <label style="font-size: 16px; font-weight: bold; display: block; margin-bottom: 8px;">Select Area</label>
          <select id="area-select" style="width: 100%; padding: 6px; border: 1px solid #ccc; border-radius: 4px;" ${this.isLoading ? 'disabled' : ''}>
            <option value="useCase" ${this.selectedArea === 'useCase' ? 'selected' : ''}>Use Case & Protected Areas</option>
            <option value="world" ${this.selectedArea === 'world' ? 'selected' : ''}>World & Protected Areas (Not Recommended)</option>
            <option value="none" ${this.selectedArea === 'none' ? 'selected' : ''}>None</option>
          </select>
          ${this.isLoading ? `
            <div style="display: flex; align-items: center; margin-top: 10px;">
              <div style="width: 20px; height: 20px; border: 2px solid #f3f3f3; border-top: 2px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin-right: 10px;"></div>
              <span>Loading data...</span>
            </div>
            <style>
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          ` : ''}
        `;

        const selectElement = div.querySelector('#area-select') as HTMLSelectElement;
        if (selectElement) {
          selectElement.addEventListener('change', (e) => {
            const value = (e.target as HTMLSelectElement).value;
            this.selectedArea = value as 'useCase' | 'world' | 'none';
            this.fetchGeoJSON();
          });
        }
      };

      updateLegendContent();

      // Store the update function so we can call it later
      (div as any).updateContent = updateLegendContent;

      return div;
    };

    this.legendControl.addTo(this.map);
  }

  private async addData() {
    if (!this.map) return;
    this.showLegend = true;
    await this.fetchGeoJSON();
    this.addLegend();
  }

  private createLegendDiv(): HTMLElement {
    const div = L.DomUtil.create('div', 'leaflet-control-legend');
    return div;
  }

  private async fetchGeoJSON() {
    if (!this.map) return;

    // Set loading state and update legend
    this.isLoading = true;
    this.updateLegendContent();

    const narbonnaiseBounds = L.latLngBounds(
      [42.8, 2.6],
      [43.3, 3.5],
    );
    const worldBounds = L.latLngBounds(
      [-90, -180],
      [90, 180],
    );

    // Logic to clear all layers when 'none' is selected
    if (this.selectedArea === 'none') {
      this.map.eachLayer((layer) => {
        if (layer instanceof L.MarkerClusterGroup) {
          this.map!.removeLayer(layer);
        }
      });
      // Also clear the specific class layers
      if (this.franceMetropolitanLayer) {
        this.map.removeLayer(this.franceMetropolitanLayer);
        this.franceMetropolitanLayer = null;
      }
      if (this.franceProtectedLayerGroup) {
        this.map.removeControl(this.protectedControl!);
        this.map.removeLayer(this.franceProtectedLayerGroup);
        this.franceProtectedLayerGroup = null;
        this.protectedLayersMap.clear();
        this.protectedControl = null;
      }

      // Clear loading state
      this.isLoading = false;
      this.updateLegendContent();
      return;
    }

    const bounds = this.selectedArea === 'useCase' ? narbonnaiseBounds : worldBounds;
    const caseStudy = Cookies.get('caseStudy');

    if (caseStudy === 'pnr_narbonnaise') {
      try {
        await this.loadGeoLayers(bounds);
        await this.franceMonuments(bounds);
      } catch (error) {
        console.error('Failed to add GeoJSON to map:', error);
      } finally {
        // Clear loading state regardless of success or error
        this.isLoading = false;
        this.updateLegendContent();
      }
    } else {
      // Clear loading state if case study doesn't match
      this.isLoading = false;
      this.updateLegendContent();
    }
  }

  private updateLegendContent() {
    if (!this.legendControl || !this.map) return;

    // Get the legend div and update its content
    const legendDiv = this.map.getContainer().querySelector('.leaflet-control-france-usecase') as HTMLElement;
    if (legendDiv && (legendDiv as any).updateContent) {
      (legendDiv as any).updateContent();
    }
  }

  private async franceMonuments(selectedBounds: L.LatLngBounds) {
    if (!this.map) return;
    this.showLegend = true;
    try {
      const [response1, response2] = await Promise.all([fetch(geojsonLayer1), fetch(geojsonLayer2)]);
      const [data1, data2] = await Promise.all([response1.json(), response2.json()]);

      const combinedFeatures = [...data1.features, ...data2.features];
      const combinedGeoData = { ...data1, features: combinedFeatures };

      this.map.eachLayer((layer) => {
        if (layer instanceof L.MarkerClusterGroup) {
          this.map!.removeLayer(layer);
        }
      });

      const markers = L.markerClusterGroup();

      const filteredGeoData = combinedGeoData.features.filter((feature: any) => {
        if (feature.geometry && feature.geometry.type === 'Point' && feature.geometry.coordinates) {
          const [lng, lat] = feature.geometry.coordinates;
          const featureBounds = L.latLngBounds([lat, lng], [lat, lng]);
          return selectedBounds.intersects(featureBounds);
        }
        return false;
      });

      const geoJsonLayer = L.geoJSON(filteredGeoData, {
        pointToLayer: (feature, latlng) => {
          return L.marker(latlng);
        },
        onEachFeature: (feature, layer) => {
          if (feature.properties) {
            const importantProperties = [
              `Nom: ${feature.properties.nom}`,
              `Adresse: ${feature.properties.adresse}`,
              `Code Postal: ${feature.properties.code_postal}`,
              `Libelle géographique: ${feature.properties.libelle_geographique}`,
              `Region: ${feature.properties.region}`,
              `Departement: ${feature.properties.departement}`,
              `Type équipement: ${feature.properties.type_equipement_ou_lieu}`,
              `Fonction 1: ${feature.properties.fonction_1}`,
              `Latitude: ${feature.properties.latitude}`,
              `Longitude: ${feature.properties.longitude}`,
            ];

            layer.bindPopup(importantProperties.join('<br />'));
          }
        },
      });

      markers.addLayer(geoJsonLayer);
      markers.addTo(this.map);
    } catch (error) {
      console.error('Error monuments france fetching or processing data:', error);
    }
  }

  private extractData(obj: any) {
    const protectionTypes = [
      obj.P1_NATURE === 'T' ? 'Nature' : null,
      obj.P2_CULTURE === 'T' ? 'Culture' : null,
      obj.P3_PAYSAGE === 'T' ? 'Paysage' : null,
      obj.P4_GEOLOGI === 'T' ? 'Geologie' : null,
      obj.P5_SPELEO === 'T' ? 'Speleo' : null,
      obj.P6_ARCHEO === 'T' ? 'Archeo' : null,
      obj.P7_PALEOB === 'T' ? 'Paleob' : null,
      obj.P8_ANTHROP === 'T' ? 'Anthrop' : null,
      obj.P9_SCIENCE === 'T' ? 'Science' : null,
      obj.P10_PUBLIC === 'T' ? 'Public' : null,
      obj.P11_DD === 'T' ? 'DD' : null,
      obj.P12_AUTRE === 'T' ? 'Autre' : null,
    ].filter(Boolean);

    return {
      name: obj.NOM_SITE,
      internationalCode: obj.ID_MNHN,
      url: obj.URL_FICHE,
      protectionType: protectionTypes.length ? protectionTypes : ['Unknown'],
    };
  }

  /**
   * Combines logic for franceShapefileLayer and franceLayers (Protected_areas.zip)
   * and applies filtering based on selectedBounds.
   */
  private async loadGeoLayers(selectedBounds: L.LatLngBounds) {
    if (!this.map) return;
    this.showLegend = true;

    // --- 1. Load franceShapefileLayer (Metropolitan Layer) ---
    try {
      if (this.franceMetropolitanLayer) {
        this.map.removeLayer(this.franceMetropolitanLayer);
        this.franceMetropolitanLayer = null;
      }

      const geojsonData = await shp(franceShapefileLayer);
      const features = Array.isArray(geojsonData) ? geojsonData.flatMap((item) => item.features) : geojsonData.features;
      const filteredData = features.filter((feature) => {
        if (feature.geometry?.type === 'Polygon') {
          return selectedBounds.intersects(L.geoJSON(feature).getBounds());
        } else if (feature.geometry?.type === 'Point') {
          const [lng, lat] = feature.geometry.coordinates;
          return selectedBounds.contains(L.latLng(lat, lng));
        }
        return false;
      });

      this.franceMetropolitanLayer = L.geoJSON(filteredData, {
        onEachFeature: (feature, layer) => {
          if (feature.properties) {
            const extracted = this.extractData(feature.properties);
            layer.bindPopup(`
                            <div>
                              <div><b>Name:</b> ${extracted.name}</div>
                              <div><b>International Code:</b> ${extracted.internationalCode}</div>
                              <div><b>Protection Type:</b> ${extracted.protectionType.join(', ')}</div>
                              <div><b>URL:</b> <a href="${extracted.url}">${extracted.name}</a></div>
                            </div>
                        `);
          }
        },
      }).addTo(this.map);
    } catch (error) {
      console.error('Error loading france metropolitan shapefile:', error);
    }

    // --- 2. Load franceLayers (Protected Areas Layer) ---
    try {
      // Remove previous control and layer group
      if (this.protectedControl) {
        this.map.removeControl(this.protectedControl);
      }
      if (this.franceProtectedLayerGroup) {
        this.map.removeLayer(this.franceProtectedLayerGroup);
      }

      // Initialize LayerGroup and add to map
      this.franceProtectedLayerGroup = new L.LayerGroup().addTo(this.map);
      this.protectedLayersMap.clear();

      const protectedGeojsonData = await shp(franceLayers);

      const items = Array.isArray(protectedGeojsonData)
        ? protectedGeojsonData
        : [{ fileName: '', features: protectedGeojsonData.features }];

      // --- Group features by Folder Name (Protection Type) ---
      const featuresByLayer = new Map<string, any[]>();

      items.forEach((item: any) => {
        const fileName: string = item.fileName || item.name || '';
        const segments = fileName.split('/').filter(Boolean);

        let folderName = 'Unknown';
        const protectedAreasIndex = segments.indexOf('Protected_areas');

        if (protectedAreasIndex !== -1 && segments.length > protectedAreasIndex + 1) {
          folderName = segments[protectedAreasIndex + 1];
        } else if (segments.length >= 1) {
          // Fallback: Use the first non-empty segment
          folderName = segments[0];
        }

        // Attach folderName to every feature and collect them by layer
        (item.features || []).forEach((feature: any) => {
          feature.properties = feature.properties || {};
          feature.properties.__PROTECTION_FOLDER = folderName;

          if (!featuresByLayer.has(folderName)) {
            featuresByLayer.set(folderName, []);
          }
          featuresByLayer.get(folderName)?.push(feature);
        });
      });

      // --- CREATE INDIVIDUAL LAYERS AND MANAGE VISIBILITY ---
      featuresByLayer.forEach((features, folderName) => {
        // Filter features by the selected bounds
        const filteredFeatures = features.filter((feature: any) => {
          if (feature.geometry) {
            try {
              const featureBounds = L.geoJSON(feature).getBounds();
              return selectedBounds.intersects(featureBounds);
            } catch (e) {
              return false;
            }
          }
          return false;
        });

        if (filteredFeatures.length > 0) {
          const protectedLayer = L.geoJSON(filteredFeatures, {
            style: (feature) => {
              return {
                color: '#0070c0',
                weight: 2,
                opacity: 0.8,
                fillColor: '#0070c0',
                fillOpacity: 0.3,
              };
            },
            onEachFeature: (feature, layer) => {
              // APPLY MAPPING FIXES (3.1-3.3)
              if (feature.properties) {
                const mapping = LAYER_MAPPINGS[folderName] || LAYER_MAPPINGS.DEFAULT;

                // Extract values using the dynamic field names
                const name = feature.properties[mapping.name] ?? 'N/A';
                const code = feature.properties[mapping.code] ?? 'N/A';
                const url = feature.properties[mapping.url] ?? '';
                const protectionFolder = folderName ?? 'Unknown';

                const urlHtml = url ? `<a href="${url}" target="_blank" rel="noopener noreferrer">Link</a>` : 'N/A';

                const popupContent = `
                                <div>
                                  <div><b>Name:</b> ${name}</div>
                                  <div><b>International code:</b> ${code}</div>
                                  <div><b>Protection type:</b> ${protectionFolder}</div>
                                  <div><b>URL:</b> ${urlHtml}</div>
                                </div>
                              `;
                layer.bindPopup(popupContent);
              }
            },
          });

          this.protectedLayersMap.set(folderName, protectedLayer);

          // 3.4 Default Visibility: Only add suggested layers to the map initially
          if (DEFAULT_VISIBLE_LAYERS.includes(folderName)) {
            this.franceProtectedLayerGroup?.addLayer(protectedLayer);
          }
        }
      });

      // Add the layer control after loading and setting up visibility
      this.addProtectedLayerControl();

    } catch (error) {
      console.error('Error loading protected layers from shapefile:', error);
    }
  }

  // --- New Layer Control Method (3.4) ---
  private addProtectedLayerControl() {
    if (!this.map || this.protectedLayersMap.size === 0) return;

    // Create the Leaflet Layers Control
    this.protectedControl = L.control.layers(
      {}, // Base layers
      {}, // Overlay layers
      { collapsed: true, position: 'topright' }
    );

    // Add all processed layers to the control
    this.protectedLayersMap.forEach((layer, layerName) => {
      this.protectedControl!.addOverlay(layer, layerName);
    });

    this.protectedControl.addTo(this.map);

    // Handle layer visibility changes manually since we're using a LayerGroup
    this.map.on('overlayadd', (e: any) => {
      const layerName = e.name;
      const layer = this.protectedLayersMap.get(layerName);
      if (layer && !this.franceProtectedLayerGroup?.hasLayer(layer)) {
        this.franceProtectedLayerGroup?.addLayer(layer);
      }
    });

    this.map.on('overlayremove', (e: any) => {
      const layerName = e.name;
      const layer = this.protectedLayersMap.get(layerName);
      if (layer && this.franceProtectedLayerGroup?.hasLayer(layer)) {
        this.franceProtectedLayerGroup?.removeLayer(layer);
      }
    });
  }
}

export default GeoJsonControl;